import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import * as XLSX from 'xlsx';
import {
  MdUploadFile, MdWarning, MdCheckCircle, MdError, MdPeople,
  MdReceipt, MdAttachMoney, MdContentCopy, MdRestartAlt, MdDownload, MdAutorenew,
  MdDeleteForever, MdRestoreFromTrash, MdDeleteSweep,
} from 'react-icons/md';
import { clientesAPI } from '../services/api';
import { useModal } from '../context/ModalContext';

const Description = styled.p`
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textSecondary};
  margin: 0 0 ${p => p.theme.spacing.xl} 0;
`;

const DropZone = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${p => p.theme.spacing.sm};
  padding: ${p => p.theme.spacing.xxl || p.theme.spacing.xl} ${p => p.theme.spacing.xl};
  border: 2px dashed ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.lg};
  background: ${p => p.theme.colors.background};
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};
  text-align: center;

  &:hover { border-color: ${p => p.theme.colors.primary}; background: ${p => p.theme.colors.primary}0d; }

  svg { font-size: 2.5rem; color: ${p => p.theme.colors.primary}; }
`;

const DropZoneTitle = styled.div`
  font-size: ${p => p.theme.fontSizes.md};
  font-weight: 600;
  color: ${p => p.theme.colors.textPrimary};
`;

const DropZoneHint = styled.div`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textLight};
`;

const HiddenInput = styled.input`
  display: none;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: ${p => p.theme.spacing.md};
  margin-bottom: ${p => p.theme.spacing.xl};
`;

const SummaryCard = styled.div`
  background: ${p => p.theme.colors.background};
  border-radius: ${p => p.theme.borderRadius.md};
  padding: ${p => p.theme.spacing.lg};
  text-align: center;
  min-width: 0;
`;

const SummaryValue = styled.div`
  font-size: ${p => p.theme.fontSizes.xxl};
  font-weight: 700;
  color: ${p => p.color || p.theme.colors.textPrimary};
  overflow-wrap: anywhere;
`;

const SummaryLabel = styled.div`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textSecondary};
  margin-top: 4px;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  margin-bottom: ${p => p.theme.spacing.xl};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;
`;

const Th = styled.th`
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.md};
  text-align: left;
  font-size: ${p => p.theme.fontSizes.xs};
  font-weight: 700;
  color: ${p => p.theme.colors.textLight};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid ${p => p.theme.colors.border};
`;

const Td = styled.td`
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.md};
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textPrimary};
  border-bottom: 1px solid ${p => p.theme.colors.borderLight};
  vertical-align: top;
`;

const NombreCell = styled.div`
  font-weight: ${p => p.esPadre ? 700 : 400};
  padding-left: ${p => p.esPadre ? '0' : '18px'};
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 99px;
  font-size: ${p => p.theme.fontSizes.xs};
  font-weight: 600;
  margin: 1px 3px 1px 0;
  background: ${p => p.tone === 'warning' ? '#fffbeb' : p.tone === 'danger' ? '#fef2f2' : p.theme.colors.primary + '15'};
  color: ${p => p.tone === 'warning' ? '#92400e' : p.tone === 'danger' ? '#991b1b' : p.theme.colors.primary};

  svg { font-size: 0.9rem; }
`;

const AccionSelect = styled.select`
  padding: 5px 8px;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.sm};
  font-size: ${p => p.theme.fontSizes.xs};
  background: ${p => p.theme.colors.backgroundLight};
  color: ${p => p.theme.colors.textPrimary};
`;

const ButtonRow = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.md};
  flex-wrap: wrap;
`;

const Button = styled.button`
  padding: 10px ${p => p.theme.spacing.xl};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 600;
  font-family: ${p => p.theme.fonts.primary};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  border: 1px solid ${p => p.theme.colors.border};
  background: ${p => p.theme.colors.backgroundLight};
  color: ${p => p.theme.colors.textPrimary};

  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const PrimaryButton = styled(Button)`
  background: linear-gradient(135deg, ${p => p.theme.colors.primary}, ${p => p.theme.colors.primaryDark});
  color: white;
  border: none;
  box-shadow: 0 4px 14px ${p => p.theme.colors.primary}40;

  &:hover:not(:disabled) { transform: translateY(-1px); }
`;

const ResultBox = styled.div`
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
  border-radius: ${p => p.theme.borderRadius.md};
  padding: ${p => p.theme.spacing.lg};
  margin-bottom: ${p => p.theme.spacing.lg};
`;

const ErrorBox = styled.div`
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
  border-radius: ${p => p.theme.borderRadius.md};
  padding: ${p => p.theme.spacing.lg};
  margin-bottom: ${p => p.theme.spacing.lg};
`;

const DangerZone = styled.div`
  margin-top: ${p => p.theme.spacing.xxl || p.theme.spacing.xl};
  padding-top: ${p => p.theme.spacing.xl};
  border-top: 1px dashed #fca5a5;
`;

const DangerTitle = styled.h4`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 700;
  color: #991b1b;
  margin: 0 0 6px;
`;

const DangerButton = styled(Button)`
  border-color: #fca5a5;
  color: #991b1b;
  background: #fef2f2;
  &:hover:not(:disabled) { background: #fee2e2; }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9500;
  padding: 16px;
`;

const ModalCard = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl};
  width: 100%;
  max-width: 420px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.2);
`;

const ModalTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: ${p => p.theme.fontSizes.md};
  font-weight: 700;
  color: #991b1b;
  margin: 0 0 10px;
`;

const ModalMessage = styled.p`
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textSecondary};
  line-height: 1.5;
  margin: 0 0 16px;
`;

const CodigoInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${p => p.hasError ? '#fca5a5' : p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  box-sizing: border-box;
  margin-bottom: 6px;
`;

const CodigoErrorMsg = styled.div`
  color: #dc2626;
  font-size: ${p => p.theme.fontSizes.xs};
  margin-bottom: 10px;
`;

const ModalActionsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${p => p.theme.spacing.sm};
  margin-top: 6px;
`;

const TrashTable = styled(Table)`
  min-width: 600px;
`;

const fmt = (n) => parseFloat(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 });

const ImportarClientesPanel = () => {
  const { toast } = useModal();
  const fileInputRef = useRef(null);

  const [cargando, setCargando] = useState(false);
  const [grupos, setGrupos] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [error, setError] = useState('');
  const [importando, setImportando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [nombreArchivo, setNombreArchivo] = useState('');
  const [descargando, setDescargando] = useState(false);

  // Papelera de reciclaje
  const [papelera, setPapelera] = useState(null);
  const [cargandoPapelera, setCargandoPapelera] = useState(false);
  const [restaurandoId, setRestaurandoId] = useState(null);
  const [modalActivo, setModalActivo] = useState(null); // 'eliminarTodos' | 'vaciarPapelera' | null
  const [codigoModal, setCodigoModal] = useState('');
  const [errorModal, setErrorModal] = useState('');
  const [procesandoModal, setProcesandoModal] = useState(false);

  const cargarPapelera = async () => {
    setCargandoPapelera(true);
    try {
      const res = await clientesAPI.getPapelera();
      setPapelera(res.data.data);
    } catch (err) {
      console.error(err);
      toast(err.response?.data?.message || 'No se pudo cargar la papelera', 'error');
    } finally {
      setCargandoPapelera(false);
    }
  };

  const abrirModal = (tipo) => {
    setModalActivo(tipo);
    setCodigoModal('');
    setErrorModal('');
  };

  const cerrarModal = () => {
    if (procesandoModal) return;
    setModalActivo(null);
    setCodigoModal('');
    setErrorModal('');
  };

  const confirmarModal = async () => {
    if (!codigoModal.trim()) {
      setErrorModal('Ingresa el código de confirmación');
      return;
    }
    setProcesandoModal(true);
    setErrorModal('');
    try {
      if (modalActivo === 'eliminarTodos') {
        const res = await clientesAPI.eliminarTodos(codigoModal);
        toast(res.data.message || 'Clientes enviados a la papelera', 'success');
        reset();
        if (papelera) await cargarPapelera();
      } else if (modalActivo === 'vaciarPapelera') {
        const res = await clientesAPI.vaciarPapelera(codigoModal);
        toast(res.data.message || 'Papelera vaciada', 'success');
        await cargarPapelera();
      }
      setModalActivo(null);
      setCodigoModal('');
    } catch (err) {
      console.error(err);
      setErrorModal(err.response?.data?.message || 'Código incorrecto');
    } finally {
      setProcesandoModal(false);
    }
  };

  const restaurarCliente = async (id) => {
    setRestaurandoId(id);
    try {
      await clientesAPI.restaurarCliente(id);
      toast('Cliente restaurado', 'success');
      await cargarPapelera();
    } catch (err) {
      console.error(err);
      toast(err.response?.data?.message || 'No se pudo restaurar el cliente', 'error');
    } finally {
      setRestaurandoId(null);
    }
  };

  const reset = () => {
    setGrupos(null);
    setResumen(null);
    setError('');
    setResultado(null);
    setNombreArchivo('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleArchivo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setResultado(null);
    setNombreArchivo(file.name);
    setCargando(true);
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const filas = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: null });

      const res = await clientesAPI.importarPreview(filas);
      setGrupos(res.data.data.grupos);
      setResumen(res.data.data.resumen);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || err.message || 'No se pudo leer el archivo');
    } finally {
      setCargando(false);
    }
  };

  const cambiarAccion = (grupoIdx, tipo, subIdx, accion) => {
    setGrupos(prev => {
      const copia = prev.map(g => ({ ...g, padre: { ...g.padre }, subclientes: g.subclientes.map(s => ({ ...s })) }));
      if (tipo === 'padre') {
        copia[grupoIdx].padre.accion = accion;
      } else {
        copia[grupoIdx].subclientes[subIdx].accion = accion;
      }
      return copia;
    });
  };

  // Aplica la misma acción a todas las filas que tienen un duplicado detectado.
  const aplicarAccionATodos = (accion) => {
    setGrupos(prev => prev.map(g => ({
      ...g,
      padre: { ...g.padre, accion: g.padre.duplicado ? accion : g.padre.accion },
      subclientes: g.subclientes.map(s => ({ ...s, accion: s.duplicado ? accion : s.accion })),
    })));
  };

  const confirmarImportacion = async () => {
    setImportando(true);
    setError('');
    try {
      const res = await clientesAPI.importarConfirmar(grupos);
      setResultado(res.data.data);
      toast('Importación completada', 'success');
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || err.message || 'Error al importar');
    } finally {
      setImportando(false);
    }
  };

  // Descarga un Excel con el mismo formato de la plantilla, prellenado con los
  // clientes ya registrados (para editarlo y volver a subirlo si hace falta).
  const descargarPlantilla = async () => {
    setDescargando(true);
    setError('');
    try {
      const res = await clientesAPI.importarExportar();
      const { grupos: gruposExistentes, anios } = res.data.data;

      if (gruposExistentes.length === 0) {
        toast('Aún no tienes clientes registrados para incluir en la plantilla', 'info');
        return;
      }

      const header = ['ENCARGADA', 'CLIENTE', 'Nombre', 'Régimen', 'RFC', 'Teléfono', 'Recurrente',
        ...anios.map(a => String(a)), 'Total Adeudo'];
      const filasHoja = [header];

      const filaDePersona = (persona, esPadre, encargada) => {
        const anioValores = anios.map(a => persona.anios[a] || '');
        return [
          esPadre ? (encargada || '') : '',
          esPadre ? 1 : '',
          persona.nombre,
          persona.regimen,
          persona.rfc,
          persona.telefono,
          persona.recurrente || '',
          ...anioValores,
          persona.totalCalculado,
        ];
      };

      gruposExistentes.forEach(g => {
        filasHoja.push(filaDePersona(g.padre, true, g.encargada));
        g.subclientes.forEach(s => filasHoja.push(filaDePersona(s, false)));
        filasHoja.push([]); // fila en blanco: separa grupos, igual que en la plantilla original
      });

      const hoja = XLSX.utils.aoa_to_sheet(filasHoja);
      const libro = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(libro, hoja, 'Clientes');
      const fecha = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(libro, `clientes-${fecha}.xlsx`);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || err.message || 'No se pudo generar la plantilla');
    } finally {
      setDescargando(false);
    }
  };

  const filas = [];
  if (grupos) {
    grupos.forEach((g, gi) => {
      filas.push({ tipo: 'padre', grupoIdx: gi, subIdx: null, persona: g.padre, grupo: g });
      g.subclientes.forEach((s, si) => {
        filas.push({ tipo: 'sub', grupoIdx: gi, subIdx: si, persona: s, grupo: g });
      });
    });
  }

  return (
    <>
      <Description>
        Sube un Excel con el formato de adeudos por cliente (columnas ENCARGADA, CLIENTE, Nombre,
        Régimen, RFC, Teléfono, Recurrente, un año por columna y Total Adeudo). Cada fila con
        "1" en la columna CLIENTE es un cliente nuevo; las filas debajo de ella (sin ese "1",
        hasta la siguiente fila en blanco) se importan como sus sub-clientes. Por cada año con
        monto se crea un recibo pendiente.
      </Description>

      <ButtonRow style={{ marginBottom: '20px' }}>
        <Button type="button" onClick={descargarPlantilla} disabled={descargando}>
          <MdDownload /> {descargando ? 'Generando...' : 'Descargar plantilla con clientes registrados'}
        </Button>
      </ButtonRow>

      {error && <ErrorBox><MdError style={{ verticalAlign: 'middle', marginRight: 6 }} />{error}</ErrorBox>}

      {resultado ? (
        <>
          <ResultBox>
            <MdCheckCircle style={{ verticalAlign: 'middle', marginRight: 6 }} />
            Importación completada: {resultado.clientesCreados} cliente(s), {resultado.subclientesCreados} sub-cliente(s)
            y {resultado.recibosCreados} recibo(s) creados.
            {resultado.recurrentesCreados > 0 && ` ${resultado.recurrentesCreados} cobro(s) recurrente(s) configurado(s).`}
            {resultado.reemplazados > 0 && ` ${resultado.reemplazados} cliente(s) reemplazado(s).`}
            {resultado.omitidos > 0 && ` ${resultado.omitidos} fila(s) omitida(s).`}
          </ResultBox>
          <PrimaryButton onClick={reset}><MdRestartAlt /> Importar otro archivo</PrimaryButton>
        </>
      ) : !grupos ? (
        <DropZone htmlFor="importar-clientes-input">
          <MdUploadFile />
          <DropZoneTitle>{cargando ? 'Leyendo archivo...' : 'Haz clic para elegir un archivo Excel'}</DropZoneTitle>
          <DropZoneHint>.xlsx o .xls</DropZoneHint>
          <HiddenInput
            id="importar-clientes-input"
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleArchivo}
            disabled={cargando}
          />
        </DropZone>
      ) : (
        <>
          <SummaryGrid>
            <SummaryCard>
              <SummaryValue><MdPeople style={{ verticalAlign: 'middle' }} /> {resumen.totalPersonas}</SummaryValue>
              <SummaryLabel>Clientes en el archivo</SummaryLabel>
            </SummaryCard>
            <SummaryCard>
              <SummaryValue><MdReceipt style={{ verticalAlign: 'middle' }} /> {resumen.totalRecibos}</SummaryValue>
              <SummaryLabel>Recibos a crear</SummaryLabel>
            </SummaryCard>
            <SummaryCard>
              <SummaryValue color={resumen.totalDuplicados > 0 ? '#F6AD55' : undefined}>
                {resumen.totalDuplicados}
              </SummaryValue>
              <SummaryLabel>Duplicados detectados</SummaryLabel>
            </SummaryCard>
            <SummaryCard>
              <SummaryValue><MdAttachMoney style={{ verticalAlign: 'middle' }} /> ${fmt(resumen.totalMonto)}</SummaryValue>
              <SummaryLabel>Monto total a importar</SummaryLabel>
            </SummaryCard>
            <SummaryCard>
              <SummaryValue><MdAutorenew style={{ verticalAlign: 'middle' }} /> {resumen.totalRecurrentes}</SummaryValue>
              <SummaryLabel>Cobros recurrentes a crear</SummaryLabel>
            </SummaryCard>
          </SummaryGrid>

          <div style={{ fontSize: '0.8rem', color: '#888', marginBottom: '12px' }}>
            Archivo: <strong>{nombreArchivo}</strong>
          </div>

          {resumen.totalDuplicados > 0 && (
            <ButtonRow style={{ marginBottom: '16px', alignItems: 'center' }}>
              <span style={{ fontSize: '0.8rem', color: '#888' }}>
                Para los {resumen.totalDuplicados} duplicado{resumen.totalDuplicados !== 1 ? 's' : ''}:
              </span>
              <Button type="button" onClick={() => aplicarAccionATodos('omitir')}>Omitir todos</Button>
              <Button type="button" onClick={() => aplicarAccionATodos('reemplazar')}>Reemplazar todos</Button>
            </ButtonRow>
          )}

          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <Th>Cliente</Th>
                  <Th>Régimen</Th>
                  <Th>RFC</Th>
                  <Th>Teléfono</Th>
                  <Th>Recurrente</Th>
                  <Th>Años con adeudo</Th>
                  <Th>Total</Th>
                  <Th>Advertencias</Th>
                  <Th>Acción</Th>
                </tr>
              </thead>
              <tbody>
                {filas.map((f, i) => {
                  const { tipo, grupoIdx, subIdx, persona, grupo } = f;
                  const esPadre = tipo === 'padre';
                  const anios = Object.keys(persona.anios || {});
                  return (
                    <tr key={i}>
                      <Td>
                        <NombreCell esPadre={esPadre}>{persona.nombre}</NombreCell>
                        {esPadre && grupo.encargada && (
                          <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: 2 }}>
                            Encargada: {grupo.encargada}
                            {grupo.asignadoANombre ? ` → ${grupo.asignadoANombre}` : ' (sin coincidencia)'}
                          </div>
                        )}
                      </Td>
                      <Td>{persona.regimen || '—'}</Td>
                      <Td>{persona.rfc || '—'}</Td>
                      <Td>{persona.telefono || '—'}</Td>
                      <Td>
                        {persona.recurrente > 0
                          ? <Badge><MdAutorenew /> ${fmt(persona.recurrente)}/mes</Badge>
                          : '—'}
                      </Td>
                      <Td>
                        {anios.length === 0 ? '—' : anios.map(a => (
                          <Badge key={a}>{a}: ${fmt(persona.anios[a])}</Badge>
                        ))}
                      </Td>
                      <Td>${fmt(persona.totalCalculado)}</Td>
                      <Td>
                        {esPadre && grupo.encargadaSinMatch && (
                          <Badge tone="warning"><MdWarning /> Encargada sin coincidencia</Badge>
                        )}
                        {persona.totalNoCuadra && (
                          <Badge tone="warning"><MdWarning /> No cuadra con Total Adeudo (${fmt(persona.totalDeclarado)})</Badge>
                        )}
                        {persona.duplicado && (
                          <Badge tone="danger">
                            <MdContentCopy /> Ya existe: {persona.duplicado.existingNombre} (saldo ${fmt(persona.duplicado.existingSaldo)})
                          </Badge>
                        )}
                      </Td>
                      <Td>
                        {persona.duplicado ? (
                          <AccionSelect
                            value={persona.accion}
                            onChange={e => cambiarAccion(grupoIdx, tipo, subIdx, e.target.value)}
                          >
                            <option value="omitir">Omitir</option>
                            <option value="reemplazar">Reemplazar</option>
                          </AccionSelect>
                        ) : (
                          <span style={{ fontSize: '0.8rem', color: '#888' }}>Crear</span>
                        )}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableWrapper>

          <ButtonRow>
            <PrimaryButton onClick={confirmarImportacion} disabled={importando}>
              <MdCheckCircle /> {importando ? 'Importando...' : 'Confirmar importación'}
            </PrimaryButton>
            <Button onClick={reset} disabled={importando}>Cancelar</Button>
          </ButtonRow>
        </>
      )}

      <DangerZone>
        <DangerTitle><MdDeleteForever /> Zona de peligro</DangerTitle>
        <Description style={{ marginBottom: '12px' }}>
          Envía todos los clientes y sub-clientes de tu empresa a la papelera de reciclaje.
          No se borran de inmediato: puedes restaurarlos o vaciar la papelera (borrado
          definitivo) desde aquí.
        </Description>

        <ButtonRow style={{ marginBottom: '16px' }}>
          <DangerButton type="button" onClick={() => abrirModal('eliminarTodos')}>
            <MdDeleteForever /> Eliminar todos los clientes y sub-clientes
          </DangerButton>
          <Button
            type="button"
            onClick={() => { if (papelera === null) cargarPapelera(); else setPapelera(null); }}
            disabled={cargandoPapelera}
          >
            <MdRestoreFromTrash /> {cargandoPapelera ? 'Cargando...' : papelera === null ? 'Ver papelera' : 'Ocultar papelera'}
          </Button>
        </ButtonRow>

        {papelera !== null && (
          <>
            {papelera.length === 0 ? (
              <Description>La papelera está vacía.</Description>
            ) : (
              <>
                <TableWrapper>
                  <TrashTable>
                    <thead>
                      <tr>
                        <Th>Cliente</Th>
                        <Th>Saldo</Th>
                        <Th>Eliminado</Th>
                        <Th>Por</Th>
                        <Th></Th>
                      </tr>
                    </thead>
                    <tbody>
                      {papelera.map(c => (
                        <tr key={c.id}>
                          <Td>
                            <NombreCell esPadre={!c.padre_cliente_id}>{c.nombre}</NombreCell>
                            {c.padre_nombre && (
                              <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: 2 }}>
                                Sub-cliente de {c.padre_nombre}
                              </div>
                            )}
                          </Td>
                          <Td>${fmt(c.saldo_pendiente)}</Td>
                          <Td>{new Date(c.eliminado_en).toLocaleString('es-MX')}</Td>
                          <Td>{c.eliminado_por_nombre || '—'}</Td>
                          <Td>
                            <Button
                              type="button"
                              onClick={() => restaurarCliente(c.id)}
                              disabled={restaurandoId === c.id}
                            >
                              <MdRestoreFromTrash /> {restaurandoId === c.id ? 'Restaurando...' : 'Restaurar'}
                            </Button>
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </TrashTable>
                </TableWrapper>

                <DangerButton type="button" onClick={() => abrirModal('vaciarPapelera')}>
                  <MdDeleteSweep /> Vaciar papelera definitivamente ({papelera.length})
                </DangerButton>
              </>
            )}
          </>
        )}
      </DangerZone>

      {modalActivo && (
        <ModalOverlay onClick={cerrarModal}>
          <ModalCard onClick={e => e.stopPropagation()}>
            {modalActivo === 'eliminarTodos' ? (
              <>
                <ModalTitle><MdDeleteForever /> Eliminar todos los clientes</ModalTitle>
                <ModalMessage>
                  Todos los clientes y sub-clientes de tu empresa se enviarán a la papelera de
                  reciclaje (no se borran todavía). Escribe el código de confirmación configurado
                  en Configuración → Pagos y Recibos para continuar.
                </ModalMessage>
              </>
            ) : (
              <>
                <ModalTitle><MdDeleteSweep /> Vaciar papelera</ModalTitle>
                <ModalMessage>
                  Esto borra <strong>definitivamente</strong> todos los clientes de la papelera junto
                  con sus recibos, pagos y cobros recurrentes. No se puede deshacer. Escribe el
                  código de confirmación para continuar.
                </ModalMessage>
              </>
            )}
            <CodigoInput
              type="password"
              autoComplete="off"
              placeholder="Código de confirmación"
              value={codigoModal}
              hasError={!!errorModal}
              onChange={e => { setCodigoModal(e.target.value); setErrorModal(''); }}
              onKeyDown={e => { if (e.key === 'Enter') confirmarModal(); }}
              disabled={procesandoModal}
              autoFocus
            />
            {errorModal && <CodigoErrorMsg>{errorModal}</CodigoErrorMsg>}
            <ModalActionsRow>
              <Button type="button" onClick={cerrarModal} disabled={procesandoModal}>Cancelar</Button>
              <DangerButton type="button" onClick={confirmarModal} disabled={procesandoModal}>
                {procesandoModal ? 'Procesando...' : 'Confirmar'}
              </DangerButton>
            </ModalActionsRow>
          </ModalCard>
        </ModalOverlay>
      )}
    </>
  );
};

export default ImportarClientesPanel;
